import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import "./BillsFilter.css";
import jsPDF from "jspdf";
import "jspdf-autotable";

/* ─── Couleurs par boutique (admin view) ─── */
const LOC_COLORS = [
  [25, 118, 210],  [56, 142, 60],  [245, 124, 0],  [123, 31, 162],
  [0, 151, 167],   [198, 40, 40],  [46, 125, 50],  [21, 101, 192],
  [230, 81, 0],    [74, 20, 140],
];

function TicketCaisse({ currentUser: propUser }) {
  const reduxUser = useSelector((s) => s.AuthReducer?.user);
  const currentUser = propUser || reduxUser;
  const isAdmin = currentUser?.role === "admin";

  const [bills, setBills]       = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [locationName, setLocationName] = useState("Non définie");

  /* ── Résolution nom boutique pour user non-admin ── */
  const resolveLocationName = useCallback((billsData) => {
    const first = billsData?.find(
      (b) => b.localisation && typeof b.localisation === "object" && b.localisation.name
    );
    if (first) return first.localisation.name;
    const loc = currentUser?.localisation;
    if (!loc) return "Non définie";
    if (typeof loc === "string" && !/^[0-9a-fA-F]{24}$/.test(loc)) return loc;
    if (typeof loc === "object" && loc.name) return loc.name;
    return "Non définie";
  }, [currentUser]);

  /* ── Chargement données ── */
  const fetchAllData = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const h = { Authorised: token };
      const [pRes, bRes, lRes] = await Promise.all([
        axios.get("/api/ProductV/getProductV", { headers: h }),
        axios.get("/api/BillsValises/getAllBillsV",  { headers: h }),
        axios.get("/api/Locations/getAllLocations",  { headers: h }),
      ]);
      const bd = bRes.data || [];
      setProducts(pRes.data || []);
      setBills(bd);
      setLocations(lRes.data || []);
      if (!isAdmin) setLocationName(resolveLocationName(bd));
    } catch (e) {
      console.error("❌ Erreur chargement :", e);
    } finally {
      setLoading(false);
    }
  }, [currentUser, isAdmin, resolveLocationName]);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  /* ── Résolution produit ── */
  const getDetails = useCallback((item) => {
    for (const p of products) {
      const sp = p.subProductsV?.find(
        (s) => s._id === item.id || s._id === String(item.id)
      );
      if (sp) return { parent: p.name, name: sp.name, price: item.price ?? sp.price };
    }
    const main = products.find((p) => p._id === item.id || p._id === String(item.id));
    if (main) return { parent: main.name, name: null, price: item.price ?? main.price };
    return { parent: null, name: item.name || "Inconnu", price: item.price || 0 };
  }, [products]);

  /* ── Filtrage jour courant ── */
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const endOfDay   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

  const dailyBills = bills.filter((b) => {
    const d = new Date(b.date?.$date || b.date);
    return !isNaN(d) && d >= startOfDay && d <= endOfDay;
  });

  /* ── Résolution nom localisation d'une facture ── */
  const getBillLocName = (bill) => {
    if (bill.localisation && typeof bill.localisation === "object" && bill.localisation.name)
      return bill.localisation.name;
    const id = typeof bill.localisation === "string"
      ? bill.localisation
      : String(bill.localisation?._id || "");
    const found = locations.find((l) => l._id === id || String(l._id) === id);
    return found?.name || "Boutique inconnue";
  };

  /* ════════════════════════════════════════════════════
     VUE ADMIN : groupement par localisation
  ════════════════════════════════════════════════════ */
  const buildAdminGroups = () => {
    const groups = {};
    dailyBills.forEach((bill) => {
      const locName = getBillLocName(bill);
      if (!groups[locName]) groups[locName] = { active: [], cancelled: [] };
      bill.items.forEach((item) => {
        const details = getDetails(item);
        if (item.isCancelled) groups[locName].cancelled.push({ bill, item, details });
        else                  groups[locName].active.push({ bill, item, details });
      });
    });
    return groups;
  };

  const adminGroups = isAdmin ? buildAdminGroups() : {};
  const adminLocNames = Object.keys(adminGroups).sort();

  const grandTotal = adminLocNames.reduce(
    (sum, loc) =>
      sum + adminGroups[loc].active.reduce(
        (s, { item, details }) => s + (details.price || 0) * (item.quantity || 1), 0
      ), 0
  );
  const grandCancelled = adminLocNames.reduce(
    (sum, loc) =>
      sum + adminGroups[loc].cancelled.reduce(
        (s, { item, details }) => s + (details.price || 0) * (item.quantity || 1), 0
      ), 0
  );

  /* ── Vue user normal ── */
  const activeItems = [];
  const cancelledItems = [];
  if (!isAdmin) {
    dailyBills.forEach((bill) => {
      bill.items.forEach((item) => {
        const details = getDetails(item);
        if (item.isCancelled) cancelledItems.push({ bill, item, details });
        else                  activeItems.push({ bill, item, details });
      });
    });
  }
  const totalActive    = activeItems.reduce((s, { item, details }) => s + (details.price || 0) * (item.quantity || 1), 0);
  const totalCancelled = cancelledItems.reduce((s, { item, details }) => s + (details.price || 0) * (item.quantity || 1), 0);

  /* ════════════════════════════════════════════════════
     PDF ADMIN : une section par boutique
  ════════════════════════════════════════════════════ */
  const generateAdminPDF = () => {
    const doc = new jsPDF();
    const dateStr = new Date().toLocaleDateString("fr-FR");

    doc.setFontSize(20);
    doc.text("RAPPORT GLOBAL DES VENTES", 105, 15, { align: "center" });
    doc.setFontSize(11);
    doc.text(`Date : ${dateStr}`, 105, 23, { align: "center" });
    doc.text(`Généré par : ${currentUser?.name || "Admin"}`, 105, 30, { align: "center" });

    /* Récap par boutique */
    doc.setFontSize(13);
    doc.setTextColor(0, 70, 140);
    doc.text("RÉCAPITULATIF PAR BOUTIQUE", 14, 42);
    const recapBody = adminLocNames.map((loc, i) => {
      const tot = adminGroups[loc].active.reduce(
        (s, { item, details }) => s + (details.price || 0) * (item.quantity || 1), 0
      );
      const ann = adminGroups[loc].cancelled.reduce(
        (s, { item, details }) => s + (details.price || 0) * (item.quantity || 1), 0
      );
      return [
        i + 1,
        loc,
        adminGroups[loc].active.length,
        `${tot.toFixed(2)} DT`,
        adminGroups[loc].cancelled.length,
        `${ann.toFixed(2)} DT`,
      ];
    });
    recapBody.push(["", "TOTAL", "", `${grandTotal.toFixed(2)} DT`, "", `${grandCancelled.toFixed(2)} DT`]);

    doc.autoTable({
      startY: 47,
      head: [["#", "Boutique", "Articles", "Ventes", "Annulés", "Annulations"]],
      body: recapBody,
      theme: "grid",
      headStyles: { fillColor: [0, 70, 140] },
      styles: { fontSize: 9 },
    });

    /* Détail par boutique */
    adminLocNames.forEach((loc, idx) => {
      const color = LOC_COLORS[idx % LOC_COLORS.length];
      const group = adminGroups[loc];
      const locTotal = group.active.reduce(
        (s, { item, details }) => s + (details.price || 0) * (item.quantity || 1), 0
      );

      doc.addPage();
      doc.setFontSize(16);
      doc.setTextColor(...color);
      doc.text(`📍 ${loc}`, 14, 15);
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total ventes : ${locTotal.toFixed(2)} DT  |  ${group.active.length} articles`, 14, 23);

      doc.setFontSize(12);
      doc.setTextColor(0, 100, 0);
      doc.text("VENTES ACTIVES", 14, 32);
      doc.autoTable({
        startY: 36,
        head: [["#", "Heure", "Produit", "Sous-produit", "Qte", "Prix", "Facture"]],
        body: group.active.map(({ bill, item, details }, i) => [
          i + 1,
          new Date(bill.date).toLocaleTimeString("fr-FR"),
          details.parent || "-",
          details.name  || "-",
          item.quantity,
          `${(details.price || 0).toFixed(2)} DT`,
          bill.billNumber,
        ]),
        theme: "grid",
        headStyles: { fillColor: color },
        styles: { fontSize: 8 },
      });

      if (group.cancelled.length > 0) {
        const y = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(12);
        doc.setTextColor(200, 0, 0);
        doc.text("ARTICLES ANNULÉS", 14, y);
        doc.autoTable({
          startY: y + 4,
          head: [["#", "Heure", "Produit", "Sous-produit", "Motif"]],
          body: group.cancelled.map(({ bill, item, details }, i) => [
            i + 1,
            new Date(bill.date).toLocaleTimeString("fr-FR"),
            details.parent || "-",
            details.name  || "-",
            item.cancelReason || "Non spécifié",
          ]),
          theme: "grid",
          headStyles: { fillColor: [200, 0, 0] },
          styles: { fontSize: 8 },
        });
      }
    });

    doc.save(`rapport-admin-${dateStr}.pdf`);
  };

  /* ── PDF user normal ── */
  const generateUserPDF = () => {
    const doc = new jsPDF();
    const dateStr = new Date().toLocaleDateString("fr-FR");
    doc.setFontSize(20);
    doc.text("RAPPORT DE CAISSE", 105, 15, { align: "center" });
    doc.setFontSize(12);
    doc.text(`Date : ${dateStr}`, 105, 25, { align: "center" });
    doc.text(`Localisation : ${locationName}`, 105, 32, { align: "center" });
    doc.text(`Généré par : ${currentUser?.name || "Utilisateur"}`, 105, 39, { align: "center" });
    doc.setFontSize(14); doc.setTextColor(0, 100, 0);
    doc.text("VENTES ACTIVES", 14, 50);
    doc.autoTable({
      startY: 55,
      head: [["#", "Heure", "Produit", "Sous-produit", "Qte", "Prix", "Facture"]],
      body: activeItems.map(({ bill, item, details }, i) => [
        i + 1, new Date(bill.date).toLocaleTimeString("fr-FR"),
        details.parent || "-", details.name || "-",
        item.quantity, `${(details.price || 0).toFixed(2)} DT`, bill.billNumber,
      ]),
      theme: "grid", headStyles: { fillColor: [0, 100, 0] },
    });
    const y = doc.lastAutoTable.finalY + 15;
    doc.setTextColor(200, 0, 0); doc.text("ARTICLES ANNULÉS", 14, y);
    doc.autoTable({
      startY: y + 5,
      head: [["#", "Heure", "Produit", "Sous-produit", "Motif"]],
      body: cancelledItems.map(({ bill, item, details }, i) => [
        i + 1, new Date(bill.date).toLocaleTimeString("fr-FR"),
        details.parent || "-", details.name || "-", item.cancelReason || "Non spécifié",
      ]),
      theme: "grid", headStyles: { fillColor: [200, 0, 0] },
    });
    const fy = doc.lastAutoTable.finalY + 15;
    doc.setTextColor(0, 0, 0);
    doc.text(`TOTAL VENTES : ${totalActive.toFixed(2)} DT`, 14, fy);
    doc.text(`ANNULATIONS  : ${totalCancelled.toFixed(2)} DT`, 14, fy + 10);
    doc.setFontSize(14);
    doc.text(`NET À ENCAISSER : ${totalActive.toFixed(2)} DT`, 14, fy + 25);
    doc.save(`rapport-caisse-${dateStr}.pdf`);
  };

  /* ════════════════════════════════════════════════════
     RENDU
  ════════════════════════════════════════════════════ */
  return (
    <div className="bills-filter">
      {/* ── En-tête ── */}
      <div className="report-header">
        <div>
          <h2>{isAdmin ? "📊 Rapport Global des Ventes" : "🧾 Rapport de Caisse du Jour"}</h2>
          <p style={{ margin: 0, fontSize: "0.9rem", opacity: 0.7 }}>
            {isAdmin
              ? `👑 Admin — ${adminLocNames.length} boutique(s) — ${new Date().toLocaleDateString("fr-FR")}`
              : `📍 ${locationName} — ${currentUser?.name || ""}`}
          </p>
        </div>
        <button
          onClick={isAdmin ? generateAdminPDF : generateUserPDF}
          className="pdf-button"
        >
          📄 Exporter PDF
        </button>
      </div>

      {loading && <p style={{ textAlign: "center" }}>⏳ Chargement...</p>}

      {/* ══ VUE ADMIN ══ */}
      {isAdmin ? (
        <>
          {/* Cartes récap globales */}
          <div className="summary-cards">
            <div className="summary-card active">
              <h3>Total Ventes</h3>
              <p className="amount">{grandTotal.toFixed(2)} DT</p>
              <p className="count">{adminLocNames.reduce((s, l) => s + adminGroups[l].active.length, 0)} articles</p>
            </div>
            <div className="summary-card cancelled">
              <h3>Total Annulations</h3>
              <p className="amount">{grandCancelled.toFixed(2)} DT</p>
              <p className="count">{adminLocNames.reduce((s, l) => s + adminGroups[l].cancelled.length, 0)} articles</p>
            </div>
            <div className="summary-card net">
              <h3>Net Toutes Boutiques</h3>
              <p className="amount">{grandTotal.toFixed(2)} DT</p>
              <p className="count">{adminLocNames.length} boutique(s)</p>
            </div>
          </div>

          {/* Section par boutique */}
          {adminLocNames.length === 0 ? (
            <p style={{ textAlign: "center", opacity: 0.6 }}>Aucune vente aujourd'hui</p>
          ) : (
            adminLocNames.map((loc, idx) => {
              const group = adminGroups[loc];
              const color = LOC_COLORS[idx % LOC_COLORS.length];
              const colorHex = `rgb(${color.join(",")})`;
              const locTotal = group.active.reduce(
                (s, { item, details }) => s + (details.price || 0) * (item.quantity || 1), 0
              );
              const locCancelled = group.cancelled.reduce(
                (s, { item, details }) => s + (details.price || 0) * (item.quantity || 1), 0
              );
              return (
                <div key={loc} style={{
                  marginBottom: 40,
                  border: `2px solid ${colorHex}`,
                  borderRadius: 10,
                  overflow: "hidden",
                }}>
                  {/* Header boutique */}
                  <div style={{
                    background: colorHex,
                    color: "#fff",
                    padding: "12px 20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <h3 style={{ margin: 0 }}>📍 {loc}</h3>
                    <div style={{ display: "flex", gap: 20, fontSize: "0.95rem" }}>
                      <span>✅ {locTotal.toFixed(2)} DT ({group.active.length} art.)</span>
                      {group.cancelled.length > 0 && (
                        <span style={{ opacity: 0.85 }}>
                          ❌ {locCancelled.toFixed(2)} DT ({group.cancelled.length} ann.)
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Tableau ventes */}
                  <div style={{ padding: "0 0 0 0" }}>
                    {group.active.length === 0 ? (
                      <p style={{ padding: 16, opacity: 0.6 }}>Aucune vente</p>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Heure</th><th>Produit</th><th>Sous-produit</th>
                            <th>Qte</th><th>Prix</th><th>Facture</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.active.map(({ bill, item, details }, i) => (
                            <tr key={i}>
                              <td>{new Date(bill.date).toLocaleTimeString("fr-FR")}</td>
                              <td>{details.parent || details.name}</td>
                              <td>{details.parent ? details.name || "-" : "-"}</td>
                              <td>{item.quantity}</td>
                              <td style={{ fontWeight: "bold" }}>{(details.price || 0).toFixed(2)} DT</td>
                              <td>#{bill.billNumber}</td>
                            </tr>
                          ))}
                          {/* Ligne sous-total */}
                          <tr style={{ background: `rgba(${color.join(",")},0.1)`, fontWeight: "bold" }}>
                            <td colSpan={4}>Sous-total {loc}</td>
                            <td style={{ color: colorHex }}>{locTotal.toFixed(2)} DT</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    )}

                    {/* Annulations boutique */}
                    {group.cancelled.length > 0 && (
                      <>
                        <p style={{ padding: "8px 12px", color: "#c00", fontWeight: "bold", margin: 0 }}>
                          🔴 Annulations ({group.cancelled.length})
                        </p>
                        <table>
                          <thead>
                            <tr><th>Heure</th><th>Produit</th><th>Sous-produit</th><th>Motif</th></tr>
                          </thead>
                          <tbody>
                            {group.cancelled.map(({ bill, item, details }, i) => (
                              <tr key={i} style={{ color: "#c00" }}>
                                <td>{new Date(bill.date).toLocaleTimeString("fr-FR")}</td>
                                <td>{details.parent || details.name}</td>
                                <td>{details.parent ? details.name || "-" : "-"}</td>
                                <td>{item.cancelReason || "Non spécifié"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {/* Bilan global */}
          {adminLocNames.length > 0 && (
            <div style={{
              background: "#1a237e",
              color: "#fff",
              borderRadius: 10,
              padding: "20px 24px",
              marginTop: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <div>
                <h3 style={{ margin: 0 }}>🏆 BILAN GLOBAL DU JOUR</h3>
                <p style={{ margin: "4px 0 0", opacity: 0.8 }}>
                  {adminLocNames.length} boutique(s) actives
                </p>
              </div>
              <div style={{ textAlign: "right" }}>
                <p style={{ margin: 0, fontSize: "2rem", fontWeight: "bold" }}>
                  {grandTotal.toFixed(2)} DT
                </p>
                <p style={{ margin: 0, opacity: 0.8, fontSize: "0.9rem" }}>
                  Net encaissé toutes boutiques
                </p>
              </div>
            </div>
          )}
        </>
      ) : (
        /* ══ VUE USER NORMAL ══ */
        <>
          <div className="summary-cards">
            <div className="summary-card active">
              <h3>Ventes Actives</h3>
              <p className="amount">{totalActive.toFixed(2)} DT</p>
              <p className="count">{activeItems.length} article{activeItems.length > 1 ? "s" : ""}</p>
            </div>
            <div className="summary-card cancelled">
              <h3>Annulations</h3>
              <p className="amount">{totalCancelled.toFixed(2)} DT</p>
              <p className="count">{cancelledItems.length} article{cancelledItems.length > 1 ? "s" : ""}</p>
            </div>
            <div className="summary-card net">
              <h3>Net à encaisser</h3>
              <p className="amount">{totalActive.toFixed(2)} DT</p>
            </div>
          </div>

          <div className="section">
            <h3>🟢 Ventes Actives</h3>
            {activeItems.length === 0 ? (
              <p style={{ textAlign: "center", opacity: 0.6 }}>Aucune vente aujourd'hui</p>
            ) : (
              <table>
                <thead>
                  <tr><th>Heure</th><th>Produit</th><th>Sous-produit</th><th>Qte</th><th>Prix</th><th>Facture</th></tr>
                </thead>
                <tbody>
                  {activeItems.map(({ bill, item, details }, i) => (
                    <tr key={i}>
                      <td>{new Date(bill.date).toLocaleTimeString("fr-FR")}</td>
                      <td>{details.parent || details.name}</td>
                      <td>{details.parent ? details.name || "-" : "-"}</td>
                      <td>{item.quantity}</td>
                      <td>{(details.price || 0).toFixed(2)} DT</td>
                      <td>#{bill.billNumber}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {cancelledItems.length > 0 && (
            <div className="section">
              <h3>🔴 Articles Annulés</h3>
              <table>
                <thead>
                  <tr><th>Heure</th><th>Produit</th><th>Sous-produit</th><th>Motif</th></tr>
                </thead>
                <tbody>
                  {cancelledItems.map(({ bill, item, details }, i) => (
                    <tr key={i} style={{ color: "#c00" }}>
                      <td>{new Date(bill.date).toLocaleTimeString("fr-FR")}</td>
                      <td>{details.parent || details.name}</td>
                      <td>{details.parent ? details.name || "-" : "-"}</td>
                      <td>{item.cancelReason || "Non spécifié"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TicketCaisse;